import { useEffect, useMemo, useRef } from "react";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  ArrowDown,
  ArrowUp,
  CalendarDays,
  GripVertical,
  ImageIcon,
  Link2,
  TriangleAlert,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  moveTimelineEventIds,
  reorderTimelineEventIds,
  type TimelineEventView,
} from "@/features/timeline";
import { cn } from "@/lib/utils";

type TimelineEventListProps = {
  events: TimelineEventView[];
  onReorderEvents: (orderedEventIds: string[]) => Promise<void> | void;
  onSelectEvent: (eventId: string) => void;
  selectedEventId: string | null;
};

type SortableTimelineEventRowProps = {
  event: TimelineEventView;
  eventButtonRefs: React.MutableRefObject<Record<string, HTMLButtonElement | null>>;
  index: number;
  onMoveEvent: (eventId: string, direction: -1 | 1) => void;
  onSelectEvent: (eventId: string) => void;
  selectedEventId: string | null;
  totalCount: number;
};

function SortableTimelineEventRow({
  event,
  eventButtonRefs,
  index,
  onMoveEvent,
  onSelectEvent,
  selectedEventId,
  totalCount,
}: SortableTimelineEventRowProps) {
  const { t } = useTranslation();
  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: event.id });

  return (
    <div
      ref={setNodeRef}
      className={cn(
        "grid gap-5 border-b border-border/75 py-6 transition-all xl:grid-cols-[2.5rem_10rem_minmax(0,1fr)_12rem]",
        event.id === selectedEventId && "xl:grid-cols-[2.5rem_10rem_minmax(0,1fr)_14rem]",
        isDragging && "opacity-80 shadow-[0_18px_42px_-22px_rgba(34,27,19,0.35)]",
      )}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <div className="flex flex-col items-center gap-2 pt-1">
        <button
          aria-label={t("timeline.list.dragHandle", { title: event.title })}
          className="rounded-full p-1 text-muted-foreground transition-colors hover:bg-secondary/70 hover:text-foreground"
          type="button"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Button
          aria-label={t("timeline.list.moveUp", { title: event.title })}
          disabled={index === 0}
          onClick={() => onMoveEvent(event.id, -1)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <ArrowUp className="h-4 w-4" />
        </Button>
        <Button
          aria-label={t("timeline.list.moveDown", { title: event.title })}
          disabled={index === totalCount - 1}
          onClick={() => onMoveEvent(event.id, 1)}
          size="icon"
          type="button"
          variant="ghost"
        >
          <ArrowDown className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-2 pt-1 text-muted-foreground">
        <div className="flex items-center gap-2 text-foreground/88">
          <CalendarDays className="h-4 w-4" />
          <p className="text-[1.95rem] font-semibold leading-none tracking-tight">
            {event.eraLabel.match(/\b(\d{4})\b/)?.[1] ?? event.eraLabel}
          </p>
        </div>
        <p className="text-base">{event.eraLabel}</p>
      </div>

      <button
        aria-current={event.id === selectedEventId ? "page" : undefined}
        className={cn(
          "group rounded-[1.4rem] px-1 py-1 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70",
          event.id === selectedEventId && "bg-secondary/22",
        )}
        onClick={() => onSelectEvent(event.id)}
        ref={(element) => {
          eventButtonRefs.current[event.id] = element;
        }}
        type="button"
      >
        <div className="flex h-full gap-5">
          <span
            aria-hidden="true"
            className="mt-1 w-1 shrink-0 rounded-full"
            style={{
              backgroundColor: [
                "#8b5cf6",
                "#10b981",
                "#ec4899",
                "#f59e0b",
                "#06b6d4",
              ][index % 5],
            }}
          />

          <div className="min-w-0 space-y-4 py-1">
            <div className="space-y-2">
              <p className="text-[2rem] font-semibold leading-none tracking-tight text-foreground">
                {event.title}
              </p>
              <p className="max-w-2xl text-lg leading-8 text-muted-foreground">
                {event.descriptionPreview}
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              {event.links[0] ? (
                <span className="inline-flex rounded-full bg-secondary/80 px-4 py-2 text-sm text-foreground/78">
                  {event.links[0].label}
                </span>
              ) : null}

              <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
                <Link2 className="h-4 w-4" />
                {t("timeline.list.linkSummary", { count: event.linkCount })}
              </span>

              {event.brokenLinkCount > 0 ? (
                <span className="inline-flex items-center gap-2 text-sm text-destructive">
                  <TriangleAlert className="h-4 w-4" />
                  {t("timeline.list.brokenSummary", { count: event.brokenLinkCount })}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      </button>

      <div className="overflow-hidden rounded-[1.4rem] border border-white/70 bg-white/75">
        {event.imageUrl ? (
          <img
            alt={event.title}
            className="h-full min-h-[10rem] w-full object-cover"
            src={event.imageUrl}
          />
        ) : (
          <div className="flex h-full min-h-[10rem] flex-col items-center justify-center gap-3 bg-secondary/30 px-4 text-center text-muted-foreground">
            <ImageIcon className="h-6 w-6" />
            <p className="text-sm">
              {t("timeline.list.noImage", { defaultValue: "No illustration attached yet" })}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export function TimelineEventList({
  events,
  onReorderEvents,
  onSelectEvent,
  selectedEventId,
}: TimelineEventListProps) {
  const { t } = useTranslation();
  const eventButtonRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 6,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );
  const orderedEventIds = useMemo(() => events.map((event) => event.id), [events]);

  useEffect(() => {
    if (!selectedEventId) {
      return;
    }

    eventButtonRefs.current[selectedEventId]?.focus();
  }, [selectedEventId]);

  function moveEvent(eventId: string, direction: -1 | 1) {
    const nextOrderedIds = moveTimelineEventIds(orderedEventIds, eventId, direction);

    if (nextOrderedIds !== orderedEventIds) {
      void onReorderEvents(nextOrderedIds);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    if (!event.over || event.active.id === event.over.id) {
      return;
    }

    const nextOrderedIds = reorderTimelineEventIds(
      orderedEventIds,
      String(event.active.id),
      String(event.over.id),
    );

    if (nextOrderedIds !== orderedEventIds) {
      void onReorderEvents(nextOrderedIds);
    }
  }

  return (
    <section className="space-y-5 border-t border-border/70 pt-6">
      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-clay">
          {t("timeline.list.eyebrow")}
        </p>
        <p className="text-sm text-muted-foreground">
          {t("timeline.list.savedCount", { count: events.length })}
        </p>
      </div>

      <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd} sensors={sensors}>
        <SortableContext items={orderedEventIds} strategy={verticalListSortingStrategy}>
          <div
            aria-label={t("timeline.list.ariaLabel")}
            className="space-y-0"
            role="list"
          >
            {events.map((event, index) => (
              <div key={event.id} role="listitem">
                <SortableTimelineEventRow
                  event={event}
                  eventButtonRefs={eventButtonRefs}
                  index={index}
                  onMoveEvent={moveEvent}
                  onSelectEvent={onSelectEvent}
                  selectedEventId={selectedEventId}
                  totalCount={events.length}
                />
              </div>
            ))}
          </div>
        </SortableContext>
      </DndContext>
    </section>
  );
}
